import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, CheckCircle, AlertCircle, Send } from '../components/icons';
import { NLPLogo } from '../components/nlp/NLPLogo';
import { supabase } from '../lib/supabase';

const TRAINING_TYPES = [
  { value: '7-day-new-model-nlp', label: '7-Day New Model of NLP' },
  { value: 'nlp-os', label: 'NLP OS' },
  { value: 'classical-nlp-practitioner', label: 'Classical NLP Practitioner Certification' },
];

interface TestimonialPageProps {
  trainingTypeOverride?: string;
  trainingLabelOverride?: string;
  description?: string;
}

export function TestimonialPage({
  trainingTypeOverride,
  trainingLabelOverride,
  description = "We'd love to hear about your experience with the 7-Day New Model of NLP training. Your words help others understand the value of this work.",
}: TestimonialPageProps = {}) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [trainingType, setTrainingType] = useState('7-day-new-model-nlp');
  const [testimonialText, setTestimonialText] = useState('');
  const [consentGiven, setConsentGiven] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    setError(null);

    if (!name.trim()) {
      setError('Please enter your name.');
      return;
    }
    if (testimonialText.trim().length < 20) {
      setError('Please share at least a few sentences about your experience.');
      return;
    }
    if (!consentGiven) {
      setError('Please check the box to confirm we may use your testimonial on our website.');
      return;
    }

    setSubmitting(true);

    try {
      const { error: insertError } = await supabase
        .from('training_testimonials')
        .insert({
          participant_name: name.trim(),
          email: email.trim() || null,
          training_type: trainingTypeOverride || trainingType,
          testimonial_text: testimonialText.trim(),
          consent_given: consentGiven,
        });

      if (insertError) throw insertError;

      setSubmitted(true);
    } catch (err) {
      console.error('Error submitting testimonial:', err);
      setError('Something went wrong. Please try again in a moment.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setName('');
    setEmail('');
    if (!trainingTypeOverride) setTrainingType('7-day-new-model-nlp');
    setTestimonialText('');
    setConsentGiven(false);
    setSubmitted(false);
    setError(null);
  };

  return (
    <div className="min-h-screen bg-nlp-cream relative overflow-hidden" style={{ fontFamily: 'Urbanist, sans-serif' }}>
      {/* Ambient glow */}
      <motion.div
        className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] rounded-full"
        style={{ background: 'radial-gradient(circle, rgba(92,122,74,0.08) 0%, transparent 70%)' }}
        animate={{ x: [0, -30, 0], y: [0, 20, 0] }}
        transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        className="absolute bottom-[-10%] left-[-5%] w-[400px] h-[400px] rounded-full"
        style={{ background: 'radial-gradient(circle, rgba(201,169,97,0.08) 0%, transparent 70%)' }}
        animate={{ x: [0, 30, 0], y: [0, -20, 0] }}
        transition={{ duration: 25, repeat: Infinity, ease: 'easeInOut' }}
      />

      {/* Header */}
      <header className="relative z-10 flex items-center justify-between px-6 md:px-10 pt-8 pb-6 max-w-[1216px] mx-auto">
        <NLPLogo className="h-12" />
        <div className="hidden md:flex items-center gap-2 px-4 py-1.5 rounded-full bg-white border border-nlp-cream-dark shadow-soft">
          <Sparkles className="w-3.5 h-3.5 text-nlp-gold-dark" />
          <span className="text-xs tracking-[0.2em] uppercase text-nlp-forest font-semibold">Share Your Experience</span>
        </div>
      </header>

      {/* Body */}
      <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-6 pb-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-2xl"
        >
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-nlp-cream rounded-full mb-4">
              <span className="text-sm font-semibold text-nlp-forest">Student Testimonial</span>
            </div>
            <h1
              className="text-nlp-forest leading-tight mb-3"
              style={{
                fontFamily: 'Georgia, "Times New Roman", serif',
                fontSize: 'clamp(26px, 3.5vw, 44px)',
                fontWeight: 700,
                letterSpacing: '-0.01em',
              }}
            >
              Share your experience with the training
            </h1>
            <p className="text-nlp-taupe text-sm md:text-base max-w-lg mx-auto leading-relaxed">
              {description}
            </p>
          </div>

          <AnimatePresence mode="wait">
            {submitted ? (
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                className="bg-white rounded-[32px] shadow-xl p-10 text-center border border-nlp-cream-dark"
              >
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                  className="w-16 h-16 bg-nlp-olive/15 rounded-2xl flex items-center justify-center mx-auto mb-5"
                >
                  <CheckCircle className="w-8 h-8 text-nlp-olive" />
                </motion.div>
                <h2 className="text-2xl font-bold text-nlp-forest mb-3">Thank you for sharing!</h2>
                <p className="text-nlp-taupe mb-6 max-w-md mx-auto">
                  Your testimonial has been received. We truly appreciate you taking the time to share your experience.
                </p>
                <button
                  onClick={handleReset}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-nlp-cream text-nlp-forest border border-nlp-cream-dark hover:bg-nlp-cream-dark rounded-full font-semibold transition-colors"
                >
                  Submit Another Testimonial
                </button>
              </motion.div>
            ) : (
              <motion.div
                key="form"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="bg-white rounded-[32px] shadow-xl p-6 md:p-8 border border-nlp-cream-dark"
              >
                {error && (
                  <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-5">
                    <AlertCircle className="w-5 h-5 flex-shrink-0" />
                    <p className="text-sm">{error}</p>
                  </div>
                )}

                {/* Name */}
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-nlp-forest mb-1.5">Your Name</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Jane Doe"
                    className="w-full px-4 py-3 border border-nlp-cream-dark rounded-xl text-sm bg-nlp-cream/60 text-nlp-forest placeholder:text-nlp-taupe/60 focus:outline-none focus:ring-2 focus:ring-nlp-olive/40 focus:border-nlp-olive transition-colors"
                    disabled={submitting}
                  />
                </div>

                {/* Email (optional) */}
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-nlp-forest mb-1.5">
                    Email <span className="text-nlp-taupe font-normal">(optional)</span>
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="jane@example.com"
                    className="w-full px-4 py-3 border border-nlp-cream-dark rounded-xl text-sm bg-nlp-cream/60 text-nlp-forest placeholder:text-nlp-taupe/60 focus:outline-none focus:ring-2 focus:ring-nlp-olive/40 focus:border-nlp-olive transition-colors"
                    disabled={submitting}
                  />
                </div>

                {/* Training type */}
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-nlp-forest mb-1.5">Training Program</label>
                  {trainingTypeOverride ? (
                    <div className="w-full px-4 py-3 border border-nlp-cream-dark rounded-xl text-sm bg-nlp-cream/30 text-nlp-forest">
                      {trainingLabelOverride}
                    </div>
                  ) : (
                    <select
                      value={trainingType}
                      onChange={(e) => setTrainingType(e.target.value)}
                      className="w-full px-4 py-3 border border-nlp-cream-dark rounded-xl text-sm bg-nlp-cream/30 text-nlp-forest cursor-pointer focus:outline-none focus:ring-2 focus:ring-nlp-olive/40 focus:border-nlp-olive transition-colors"
                      disabled={submitting}
                    >
                      {TRAINING_TYPES.map((t) => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                  )}
                </div>

                {/* Testimonial text */}
                <div className="mb-5">
                  <label className="block text-sm font-semibold text-nlp-forest mb-1.5">
                    Your Testimonial <span className="text-nlp-taupe font-normal">(required)</span>
                  </label>
                  <textarea
                    value={testimonialText}
                    onChange={(e) => setTestimonialText(e.target.value)}
                    placeholder="Share your experience of the training — what changed for you, what you gained, what you'd want others to know..."
                    rows={6}
                    className="w-full px-4 py-3 border border-nlp-cream-dark rounded-xl text-sm bg-nlp-cream/60 text-nlp-forest placeholder:text-nlp-taupe/60 focus:outline-none focus:ring-2 focus:ring-nlp-olive/40 focus:border-nlp-olive transition-colors resize-none"
                    disabled={submitting}
                  />
                  <p className="text-xs text-nlp-taupe mt-1">{testimonialText.length} characters</p>
                </div>

                {/* Consent checkbox */}
                <div className="mb-6">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={consentGiven}
                      onChange={(e) => setConsentGiven(e.target.checked)}
                      disabled={submitting}
                      className="mt-1 w-5 h-5 rounded border-nlp-cream-dark text-nlp-olive focus:ring-nlp-olive/40 cursor-pointer"
                    />
                    <span className="text-sm text-nlp-forest/80 leading-relaxed">
                      I give NLP OS permission to use my testimonial, including my name, on their website and in marketing materials.
                    </span>
                  </label>
                </div>

                {/* Submit */}
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-nlp-forest text-white hover:bg-nlp-forest-light rounded-full font-semibold transition-colors disabled:opacity-50"
                >
                  {submitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Submit My Testimonial
                    </>
                  )}
                </motion.button>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Footer */}
        <p className="relative z-10 text-xs tracking-[0.2em] uppercase text-nlp-taupe/40 mt-10">
          my.nlpos.app
        </p>
      </div>
    </div>
  );
}
